import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Trip } from "@/entities/trip";
import { listMyAgentTrips } from "@/features/trips";
import { splitAgentTrips } from "@/features/trips/lib/offerPolicy";
import { offerWaveKey, rememberEvent, subscribeOffers, subscribeResync } from "@/shared/realtime";
import { env } from "@/shared/constants/env";

/**
 * Les courses de la Main — offres comprises. Contrat du 2026-08-28.
 *
 * ## La règle d'or, appliquée ici
 * Trois canaux portent une offre, et un seul dit la vérité :
 *
 *   • le SOCKET est instantané, mais muet si l'app est fermée ;
 *   • la PUSH arrive app fermée, mais n'est jamais rejouée et son échec est
 *     avalé ;
 *   • `GET /trips` est l'état vrai.
 *
 * **Le socket et la push sont des réveils. La liste est la vérité.** Ce hook ne
 * construit donc RIEN à partir d'un événement reçu : `trip.agent.offered`
 * déclenche un rechargement, et c'est la réponse HTTP qui peuple l'écran. Cette
 * seule discipline absorbe les doublons, les événements manqués, les
 * reconnexions et les notifications perdues.
 *
 * ## Pourquoi un sondage, alors qu'on a le temps réel
 * Le contrat l'exige explicitement, toutes les 20 à 30 secondes tant qu'un
 * écran de Main est ouvert — « ce n'est pas de la paresse : c'est ce qui rend
 * le système solide quand le socket est tombé sans le dire et que la push n'est
 * jamais partie ».
 *
 * Il y a une seconde raison, décisive : **aucun événement ne signale une
 * affectation directe.** Une Main posée sur une course par un administrateur
 * n'apprend rien — ni socket, ni push. Seule la liste le révèle.
 */

export const AGENT_TRIPS_QUERY_KEY = ["agent", "trips", "feed"] as const;

/** Cadence imposée par le contrat (20 à 30 s). */
const FEED_POLL_MS = 25_000;

function useAgentTripsQuery() {
  return useQuery({
    queryKey: AGENT_TRIPS_QUERY_KEY,
    // AUCUN `agent_id` : il est silencieusement ignoré pour une Main, l'identité
    // venant du jeton. Le passer entretiendrait l'illusion d'un filtrage.
    queryFn: () => listMyAgentTrips(),
    staleTime: 5_000,
    refetchInterval: FEED_POLL_MS,
    // Retour au premier plan : le contrat le liste explicitement.
    refetchOnWindowFocus: true,
  });
}

export type AgentTripsFeed = {
  /** Offres ouvertes, la plus urgente d'abord. */
  offers: Trip[];
  /** Courses réellement attribuées, la plus récente d'abord. */
  assigned: Trip[];
  isPending: boolean;
  isError: boolean;
  /**
   * La liste est vide ET la requête a abouti.
   *
   * ⚠️ Ce n'est PAS une preuve d'absence : un `200` avec `data: []` est aussi ce
   * que renvoie l'échec de résolution d'identité quand agent-service est
   * injoignable, et les deux cas sont indiscernables. L'écran doit donc rester
   * prudent dans sa formulation — « rien pour l'instant », jamais « vous n'avez
   * aucune course ».
   */
  isEmptyButUnproven: boolean;
  refetch: () => void;
};

/**
 * Flux unique de l'espace agent : une requête, deux vues.
 *
 * Offres et courses attribuées viennent de la MÊME réponse — `GET /trips` rend
 * leur union — et partagent donc la même clé de cache : deux écrans montés en
 * même temps ne déclenchent qu'un appel.
 */
export function useAgentTripsFeed(): AgentTripsFeed {
  const query = useAgentTripsQuery();
  const qc = useQueryClient();

  const reload = useMemo(
    () => () => {
      void qc.invalidateQueries({ queryKey: AGENT_TRIPS_QUERY_KEY });
    },
    [qc],
  );

  // ── Réveil par le socket ────────────────────────────────────────────────
  // On ne lit RIEN de la charge utile pour peupler l'écran : elle ne porte
  // aucune donnée métier, et la liste tranchera.
  useEffect(() => {
    return subscribeOffers((offer) => {
      if (env.isDev) console.warn("[offres] réveil socket pour", offer.trip_id);

      // ⚠️ SEULE exception à « on ne lit rien de la charge utile ».
      //
      // `wave_size` — le nombre de Mains ayant reçu cette même offre — n'est
      // porté par AUCUNE route : `GET /trips` l'ignore. Ce n'est pas de l'état
      // (la liste reste la vérité sur ce qui existe), c'est un fait sur
      // l'offre, et le seul moment où il est prononcé.
      //
      // Il compte parce qu'il change la lecture d'un échec : sans lui, un
      // refus d'acceptation ressemble à une panne ; avec lui, la Main sait
      // qu'elles étaient trois dessus.
      if (offer.trip_id && typeof offer.wave_size === "number" && offer.wave_size > 1) {
        rememberEvent(offerWaveKey(offer.trip_id), offer.wave_size);
      }

      reload();
    });
  }, [reload]);

  // ── Reprise de session non rejouée ──────────────────────────────────────
  // `ready { recovered: false }` : les événements de la coupure sont perdus
  // pour toujours. Une offre a pu naître et mourir pendant ce temps.
  useEffect(() => subscribeResync(reload), [reload]);

  const { offers, assigned } = useMemo(
    () => splitAgentTrips(query.data, Date.now()),
    [query.data],
  );

  return {
    offers,
    assigned,
    isPending: query.isPending,
    isError: query.isError,
    isEmptyButUnproven:
      query.isSuccess && (query.data?.length ?? 0) === 0,
    refetch: reload,
  };
}
